import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaFileDownload,
  FaBalanceScale,
  FaChartLine,
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSyncAlt,
  FaShieldAlt
} from "react-icons/fa";

export default function AdminReports() {
  const [activeTab, setActiveTab] = useState("sales"); // sales, trial_balance, pnl, balance_sheet, reconciliation
  const [loading, setLoading] = useState(false);

  // Data states
  const [orders, setOrders] = useState([]);
  const [trialBalance, setTrialBalance] = useState(null);
  const [pnlData, setPnlData] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);

  // Date filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const loadDataForTab = (tab) => {
    setLoading(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    if (tab === "sales") {
      axios.get("/api/view-orders").then((res) => {
        if (res.data.status === 200) {
          setOrders(res.data.orders || []);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    } else if (tab === "trial_balance") {
      axios.get("/api/admin/accounting/trial-balance", { params }).then((res) => {
        if (res.data.status === 200) {
          setTrialBalance(res.data.data);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    } else if (tab === "pnl") {
      axios.get("/api/admin/accounting/profit-loss", { params }).then((res) => {
        if (res.data.status === 200) {
          setPnlData(res.data.data);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    } else if (tab === "balance_sheet") {
      axios.get("/api/admin/accounting/balance-sheet", { params }).then((res) => {
        if (res.data.status === 200) {
          setBalanceSheet(res.data.data);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    } else if (tab === "reconciliation") {
      axios.get("/api/admin/accounting/reconciliation").then((res) => {
        if (res.data.status === 200) {
          setReconciliation(res.data.reconciliation);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  };

  useEffect(() => {
    document.title = "Financial Statements & Analytics — Admin";
    loadDataForTab(activeTab);
  }, [activeTab, startDate, endDate]);

  // CSV Export for Orders
  const exportCSV = () => {
    if (orders.length === 0) return;
    const headers = ["Order ID", "Tracking No", "Customer", "Email", "City", "Payment", "Items", "Total (PKR)", "Status", "Date"];
    const rows = orders.map((o) => [
      o.id,
      o.tracking_no,
      `"${o.firstname} ${o.lastname}"`,
      o.email,
      o.city,
      o.payment_mode,
      o.items_count || 1,
      o.total_amount,
      o.status_label || "Active",
      `"${new Date(o.created_at).toISOString()}"`,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `store_sales_report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalOrders = orders.length;
  const totalRevenue = orders.reduce((acc, o) => acc + Number(o.total_amount || 0), 0);
  const aov = totalOrders ? Math.round(totalRevenue / totalOrders) : 0;

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: "var(--admin-text-main)" }}>
            Financial Statements & Store Reports
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>
            Double-entry Trial Balance, P&L Statement, Balance Sheet, and Order Analytics
          </p>
        </div>

        {activeTab === "sales" && (
          <button
            type="button"
            onClick={exportCSV}
            className="btn btn-dark rounded-pill px-4 py-2 fw-semibold d-flex align-items-center gap-2"
            style={{ fontSize: "13px" }}
          >
            <FaFileDownload /> Export Sales CSV
          </button>
        )}
      </div>

      {/* Tabs Navigation */}
      <div className="admin-card p-2 mb-4 bg-white">
        <ul className="nav nav-pills gap-2 flex-wrap">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link rounded-pill px-4 fw-semibold ${activeTab === "sales" ? "active bg-primary text-white" : "text-muted"}`}
              style={{ fontSize: "13px" }}
              onClick={() => setActiveTab("sales")}
            >
              <FaChartLine className="me-2" /> Sales Performance
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link rounded-pill px-4 fw-semibold ${activeTab === "trial_balance" ? "active bg-primary text-white" : "text-muted"}`}
              style={{ fontSize: "13px" }}
              onClick={() => setActiveTab("trial_balance")}
            >
              <FaBalanceScale className="me-2" /> Trial Balance
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link rounded-pill px-4 fw-semibold ${activeTab === "pnl" ? "active bg-primary text-white" : "text-muted"}`}
              style={{ fontSize: "13px" }}
              onClick={() => setActiveTab("pnl")}
            >
              <FaFileInvoiceDollar className="me-2" /> Profit & Loss (P&L)
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link rounded-pill px-4 fw-semibold ${activeTab === "balance_sheet" ? "active bg-primary text-white" : "text-muted"}`}
              style={{ fontSize: "13px" }}
              onClick={() => setActiveTab("balance_sheet")}
            >
              <FaShieldAlt className="me-2" /> Balance Sheet
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link rounded-pill px-4 fw-semibold ${activeTab === "reconciliation" ? "active bg-primary text-white" : "text-muted"}`}
              style={{ fontSize: "13px" }}
              onClick={() => setActiveTab("reconciliation")}
            >
              <FaSyncAlt className="me-2" /> Reconciliation
            </button>
          </li>
        </ul>
      </div>

      {/* Date Range Filter (For Financial Statements) */}
      {activeTab !== "reconciliation" && activeTab !== "sales" && (
        <div className="admin-card p-3 mb-4 bg-white">
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-4">
              <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "11.5px" }}>
                REPORTING START DATE
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "11.5px" }}>
                REPORTING END DATE
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-4 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-sm btn-light border w-100 fw-semibold"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                }}
              >
                Clear Date Filter (All Time)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 1: Sales Performance */}
      {activeTab === "sales" && (
        <div>
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-4">
              <div className="admin-card p-3 mb-0 bg-white">
                <span className="text-muted fw-semibold d-block mb-1" style={{ fontSize: "12px" }}>
                  GROSS ORDER REVENUE
                </span>
                <h4 className="fw-bold mb-0 text-primary">
                  Rs. {Number(totalRevenue).toLocaleString()}
                </h4>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="admin-card p-3 mb-0 bg-white">
                <span className="text-muted fw-semibold d-block mb-1" style={{ fontSize: "12px" }}>
                  TOTAL ORDERS PROCESSED
                </span>
                <h4 className="fw-bold mb-0 text-dark">{totalOrders} Orders</h4>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="admin-card p-3 mb-0 bg-white">
                <span className="text-muted fw-semibold d-block mb-1" style={{ fontSize: "12px" }}>
                  AVERAGE ORDER VALUE (AOV)
                </span>
                <h4 className="fw-bold mb-0 text-success">
                  Rs. {Number(aov).toLocaleString()}
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: Trial Balance Statement */}
      {activeTab === "trial_balance" && (
        <div className="admin-card overflow-hidden bg-white">
          <div className="p-4 border-bottom d-flex justify-content-between align-items-center">
            <div>
              <h5 className="fw-bold mb-1">Double-Entry Trial Balance</h5>
              <small className="text-muted">Summary of all debit and credit balances in Chart of Accounts</small>
            </div>
            {trialBalance && (
              <span className={`badge rounded-pill px-3 py-2 ${trialBalance.is_balanced ? "bg-success text-white" : "bg-danger text-white"}`}>
                {trialBalance.is_balanced ? "✓ Balanced (Debits = Credits)" : "! Variance Detected"}
              </span>
            )}
          </div>

          {loading ? (
            <div className="py-5 text-center">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : !trialBalance || trialBalance.accounts.length === 0 ? (
            <div className="p-5 text-center text-muted">No journal transactions recorded yet.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-0">
                <thead className="table-light text-uppercase" style={{ fontSize: "12px" }}>
                  <tr>
                    <th>Account Code</th>
                    <th>Account Title</th>
                    <th>Type</th>
                    <th className="text-end text-success" style={{ width: "180px" }}>Debit (PKR)</th>
                    <th className="text-end text-danger" style={{ width: "180px" }}>Credit (PKR)</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: "13.5px" }}>
                  {trialBalance.accounts.map((row, idx) => (
                    <tr key={idx}>
                      <td><code>{row.account_code}</code></td>
                      <td className="fw-bold text-dark">{row.account_name}</td>
                      <td><span className="badge bg-light text-muted border">{row.account_type}</span></td>
                      <td className="text-end fw-semibold text-success">
                        {row.debit > 0 ? `Rs. ${Number(row.debit).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}
                      </td>
                      <td className="text-end fw-semibold text-danger">
                        {row.credit > 0 ? `Rs. ${Number(row.credit).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light fw-bold fs-6">
                  <tr>
                    <td colSpan="3" className="text-end text-uppercase">Total Trial Balance:</td>
                    <td className="text-end text-success">
                      Rs. {Number(trialBalance.total_debit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="text-end text-danger">
                      Rs. {Number(trialBalance.total_credit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: Profit & Loss Statement (P&L) */}
      {activeTab === "pnl" && (
        <div className="admin-card p-4 bg-white" style={{ maxWidth: "800px", margin: "0 auto" }}>
          <div className="text-center pb-3 border-bottom mb-4">
            <h4 className="fw-bold mb-1">Statement of Profit & Loss</h4>
            <span className="text-muted" style={{ fontSize: "13px" }}>Operating Financial Statement</span>
          </div>

          {loading ? (
            <div className="py-5 text-center">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : !pnlData ? (
            <div className="p-5 text-center text-muted">No profit & loss data available.</div>
          ) : (
            <div style={{ fontSize: "14px" }}>
              {/* Revenue */}
              <h6 className="fw-bold text-uppercase text-primary mb-3">1. Operating Revenue</h6>
              <div className="d-flex justify-content-between py-1 border-bottom">
                <span>Gross Product Sales:</span>
                <span className="fw-semibold">Rs. {Number(pnlData.revenue.gross_sales).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="d-flex justify-content-between py-1 border-bottom text-muted">
                <span>Less: Sales Discounts & Coupons:</span>
                <span className="text-danger">-Rs. {Number(pnlData.revenue.discounts).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="d-flex justify-content-between py-1 border-bottom text-muted">
                <span>Less: Customer Returns & Refunds:</span>
                <span className="text-danger">-Rs. {Number(pnlData.revenue.returns_and_refunds).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="d-flex justify-content-between py-1 border-bottom">
                <span>Plus: Shipping & Delivery Revenue:</span>
                <span className="fw-semibold">Rs. {Number(pnlData.revenue.shipping_revenue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="d-flex justify-content-between py-2 bg-light px-2 rounded fw-bold my-2">
                <span>Total Net Revenue:</span>
                <span className="text-primary">Rs. {Number(pnlData.revenue.total_revenue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              {/* COGS */}
              <h6 className="fw-bold text-uppercase text-danger mt-4 mb-3">2. Cost of Goods Sold (COGS)</h6>
              <div className="d-flex justify-content-between py-1 border-bottom">
                <span>Wholesale Product Acquisition Cost:</span>
                <span className="text-danger">Rs. {Number(pnlData.cost_of_goods_sold).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="d-flex justify-content-between py-2 bg-light px-2 rounded fw-bold my-2">
                <span>Gross Profit:</span>
                <span className="text-success">Rs. {Number(pnlData.gross_profit).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              {/* Operating Expenses */}
              <h6 className="fw-bold text-uppercase text-warning mt-4 mb-3">3. Operating & Administrative Expenses</h6>
              {pnlData.operating_expenses.items.length === 0 ? (
                <div className="text-muted small py-1">No additional operating expenses recorded.</div>
              ) : (
                pnlData.operating_expenses.items.map((exp, i) => (
                  <div key={i} className="d-flex justify-content-between py-1 border-bottom">
                    <span>{exp.account_name}:</span>
                    <span>Rs. {Number(exp.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                ))
              )}
              <div className="d-flex justify-content-between py-1 border-bottom text-muted">
                <span>Total Operating Expenses:</span>
                <span>Rs. {Number(pnlData.operating_expenses.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              {/* Net Profit */}
              <div className="d-flex justify-content-between py-3 px-3 bg-dark text-white rounded mt-4 fs-5 fw-bold">
                <span>NET OPERATING PROFIT:</span>
                <span className={pnlData.net_profit >= 0 ? "text-success" : "text-danger"}>
                  Rs. {Number(pnlData.net_profit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: Balance Sheet */}
      {activeTab === "balance_sheet" && (
        <div className="admin-card p-4 bg-white" style={{ maxWidth: "800px", margin: "0 auto" }}>
          <div className="text-center pb-3 border-bottom mb-4">
            <h4 className="fw-bold mb-1">Balance Sheet Statement</h4>
            <span className="text-muted" style={{ fontSize: "13px" }}>Assets = Liabilities + Owner Equity</span>
          </div>

          {loading ? (
            <div className="py-5 text-center">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : !balanceSheet ? (
            <div className="p-5 text-center text-muted">No balance sheet data available.</div>
          ) : (
            <div style={{ fontSize: "14px" }}>
              {/* Assets */}
              <h6 className="fw-bold text-uppercase text-primary mb-3">Assets</h6>
              {balanceSheet.assets.items.map((a, i) => (
                <div key={i} className="d-flex justify-content-between py-1 border-bottom">
                  <span>{a.account_code} — {a.account_name}:</span>
                  <span className="fw-semibold">Rs. {Number(a.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div className="d-flex justify-content-between py-2 bg-light px-2 rounded fw-bold my-2">
                <span>Total Assets:</span>
                <span className="text-primary">Rs. {Number(balanceSheet.assets.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              {/* Liabilities */}
              <h6 className="fw-bold text-uppercase text-danger mt-4 mb-3">Liabilities</h6>
              {balanceSheet.liabilities.items.map((l, i) => (
                <div key={i} className="d-flex justify-content-between py-1 border-bottom">
                  <span>{l.account_code} — {l.account_name}:</span>
                  <span className="fw-semibold">Rs. {Number(l.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div className="d-flex justify-content-between py-2 bg-light px-2 rounded fw-bold my-2">
                <span>Total Liabilities:</span>
                <span className="text-danger">Rs. {Number(balanceSheet.liabilities.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              {/* Equity */}
              <h6 className="fw-bold text-uppercase text-dark mt-4 mb-3">Owner Equity</h6>
              {balanceSheet.equity.items.map((e, i) => (
                <div key={i} className="d-flex justify-content-between py-1 border-bottom">
                  <span>{e.account_code} — {e.account_name}:</span>
                  <span className="fw-semibold">Rs. {Number(e.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div className="d-flex justify-content-between py-2 bg-light px-2 rounded fw-bold my-2">
                <span>Total Equity:</span>
                <span className="text-dark">Rs. {Number(balanceSheet.equity.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              {/* Total Liabilities + Equity */}
              <div className="d-flex justify-content-between py-3 px-3 bg-dark text-white rounded mt-4 fs-5 fw-bold">
                <span>TOTAL LIABILITIES & EQUITY:</span>
                <span className="text-success">
                  Rs. {Number(balanceSheet.total_liabilities_and_equity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 5: Reconciliation */}
      {activeTab === "reconciliation" && (
        <div className="admin-card p-4 bg-white">
          <h5 className="fw-bold mb-3">System-Wide Financial Reconciliation</h5>
          {loading ? (
            <div className="py-5 text-center">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : !reconciliation ? (
            <div className="p-5 text-center text-muted">No reconciliation report available.</div>
          ) : (
            <div className="row g-4">
              <div className="col-12 col-md-6">
                <div className="p-3 border rounded-3 bg-light">
                  <h6 className="fw-bold mb-3">Orders vs Sales Recognized</h6>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Active Orders Gross:</span>
                    <span className="fw-bold">Rs. {Number(reconciliation.orders.gross_volume).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Recognized Sales (Invoices):</span>
                    <span className="fw-bold text-primary">Rs. {Number(reconciliation.sales.recognized_revenue).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="p-3 border rounded-3 bg-light">
                  <h6 className="fw-bold mb-3">Double-Entry Journal Status</h6>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total Posted Journal Debits:</span>
                    <span className="fw-bold text-success">Rs. {Number(reconciliation.accounting_ledger.total_debits).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total Posted Journal Credits:</span>
                    <span className="fw-bold text-danger">Rs. {Number(reconciliation.accounting_ledger.total_credits).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Integrity Status:</span>
                    <span className="badge bg-success text-white">Strictly Balanced</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
